import React from 'react';
import { Home, type LucideIcon } from 'lucide-react';
import FadeIn from '@/components/animations/FadeIn';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import DashboardButton from '@/components/ui/DashboardButton';

interface DashboardHeaderProps {
  title?: string;
  mobileTitle?: string; // New prop for mobile-specific title
  description?: string; // Keeping prop for backward compatibility
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  primaryAction?: {
    label: string;
    icon?: LucideIcon;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'default' | 'destructive';
  };
  secondaryAction?: {
    label: string;
    icon?: LucideIcon;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'default' | 'destructive';
  };
  hideOnMobile?: boolean; // New prop to control mobile visibility
}

const DashboardHeader = ({
  title = "Dashboard",
  mobileTitle,
  description, // Kept for backward compatibility but not used
  icon = <Home size={24} />,
  actions,
  primaryAction,
  secondaryAction,
  hideOnMobile = true // Default to current behavior (hide on mobile)
}: DashboardHeaderProps) => {
  const { isMobile } = useIsMobile();

  // On mobile, conditionally hide header based on hideOnMobile prop
  if (isMobile && hideOnMobile) {
    if (!actions && !primaryAction && !secondaryAction) {
      return null; // No header at all if no actions
    }

    return (
      <div className="mb-3 md:mb-4">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col gap-3 w-full"
        >
          {primaryAction && (
            <DashboardButton
              onClick={primaryAction.onClick}
              icon={primaryAction.icon}
              className="w-full h-12 text-base font-medium"
              data-test-id={`mobile-${primaryAction.label.toLowerCase().replace(/\s+/g, '-')}-button`}
            >
              {primaryAction.label}
            </DashboardButton>
          )}
          
          {secondaryAction && (
            <DashboardButton
              onClick={secondaryAction.onClick}
              icon={secondaryAction.icon}
              className="w-full h-12 text-base font-medium"
              data-test-id={`mobile-${secondaryAction.label.toLowerCase().replace(/\s+/g, '-')}-button`}
            >
              {secondaryAction.label}
            </DashboardButton>
          )}
          
          {actions}
        </motion.div>
      </div>
    );
  }

  // If on mobile but hideOnMobile is false, show full desktop header
  if (isMobile && !hideOnMobile) {
    // Show full desktop-style header on mobile when hideOnMobile is false
  }

  // Desktop version - full header with decorative elements
  return (
    <div className="mb-3 md:mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-accent/10 via-purple-500/10 to-transparent dark:from-accent/20 dark:via-purple-500/20 dark:to-transparent/10 border border-accent/20 dark:border-white/20 shadow-lg">
      <div className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] 
          from-purple-500/20 
          via-accent/10 
          to-transparent 
          dark:from-purple-500/30
          dark:via-accent/20
          dark:to-transparent
          opacity-40 rounded-3xl" />
        <div className="absolute inset-0 backdrop-blur-md bg-white/5 dark:bg-black/10 rounded-3xl" />
        
        <div className="relative px-8 py-6 flex items-center gap-4">
          <motion.div 
            className={`${isMobile ? 'h-12 w-12' : 'h-14 w-14'} flex-shrink-0 rounded-xl bg-brand-gradient flex items-center justify-center text-white shadow-xl shadow-accent/20 border border-white/20`}
            initial={{ rotate: -10, scale: 0.9 }}
            animate={{ rotate: -3, scale: 1 }}
            whileHover={{ rotate: 0, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            {icon}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="min-w-0 flex-grow"
          >
            <div className="flex flex-col">
              <h1 className={`${isMobile ? 'text-lg' : 'text-3xl'} font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent drop-shadow-sm truncate`}>
                {title}
              </h1>
              {description && !isMobile && (
                <p className="text-sm text-muted-foreground/80 font-medium">
                  {description}
                </p>
              )}
            </div>
          </motion.div>
          
          {(actions || primaryAction || secondaryAction) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="flex items-center gap-3 z-10"
            >
              {secondaryAction && (
                <DashboardButton
                  onClick={secondaryAction.onClick}
                  icon={secondaryAction.icon}
                  size="default"
                  className="px-4 py-2"
                  data-test-id={`desktop-${secondaryAction.label.toLowerCase().replace(/\s+/g, '-')}-button`}
                >
                  {secondaryAction.label}
                </DashboardButton>
              )}
              
              {primaryAction && (
                <DashboardButton
                  onClick={primaryAction.onClick}
                  icon={primaryAction.icon}
                  size="default"
                  className="px-4 py-2"
                  data-test-id={`desktop-${primaryAction.label.toLowerCase().replace(/\s+/g, '-')}-button`}
                >
                  {primaryAction.label}
                </DashboardButton>
              )}
              
              {actions}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
